import React, { useState } from 'react';
import apiService, { EmbeddingItem, EmbeddingsResponse } from '../services/api';
import { cosineSimilarity, l2Norm } from '../utils/vectors';
import './EmbeddingsExplorer.css';

const PREVIEW_DIMENSIONS = 8;
const MAX_ITEMS = 100;

function splitLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

// Similar pairs fill with the DG blue over the card colour, so the cell reads in
// both modes. Cosine between two cargo embeddings rarely drops below zero, so the
// scale runs from 0 to 1 and clamps anything outside. Past the midpoint the fill
// is too dark for the page text, so the cell switches to white.
function similarityStyle(cosine: number): React.CSSProperties {
  const t = Math.max(0, Math.min(1, cosine));
  return {
    background: `color-mix(in srgb, var(--dg-blue) ${Math.round(t * 85)}%, var(--card))`,
    color: t > 0.55 ? '#ffffff' : 'var(--foreground-color)',
  };
}

function formatValue(value: number): string {
  return value.toFixed(4);
}

const EmbeddingRow: React.FC<{ item: EmbeddingItem; index: number }> = ({ item, index }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyVector = async () => {
    if (!item.embedding) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(item.embedding));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Clipboard write failed:', err);
    }
  };

  if (!item.embedding) {
    return (
      <div className="embedding-row empty">
        <div className="embedding-row-header">
          <span className="embedding-index">#{index + 1}</span>
          <span className="embedding-text">{item.text}</span>
        </div>
        <div className="embedding-meta">No embedding: the text had nothing the encoder could use.</div>
      </div>
    );
  }

  const preview = item.embedding.slice(0, PREVIEW_DIMENSIONS);

  return (
    <div className="embedding-row">
      <div className="embedding-row-header">
        <span className="embedding-index">#{index + 1}</span>
        <span className="embedding-text">{item.text}</span>
      </div>
      <div className="embedding-meta">
        <span>{item.dimensions} dimensions</span>
        <span>L2 norm {l2Norm(item.embedding).toFixed(4)}</span>
        <button type="button" className="btn btn-link link-button" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Hide full vector' : 'Show full vector'}
        </button>
        <button type="button" className="btn btn-link link-button" onClick={copyVector}>
          {copied ? 'Copied' : 'Copy as JSON'}
        </button>
      </div>
      <code className="embedding-preview">
        [{preview.map(formatValue).join(', ')}{item.dimensions > PREVIEW_DIMENSIONS ? ', …' : ''}]
      </code>
      {expanded && (
        <pre className="embedding-full">{item.embedding.map(formatValue).join(', ')}</pre>
      )}
    </div>
  );
};

const SimilarityMatrix: React.FC<{ items: EmbeddingItem[] }> = ({ items }) => {
  const embedded = items.filter((item) => item.embedding !== null);

  if (embedded.length < 2) {
    return null;
  }

  return (
    <div className="similarity-section">
      <h3 className="h5">Pairwise cosine similarity</h3>
      <div className="similarity-scroll">
        <table className="similarity-table">
          <thead>
            <tr>
              <th></th>
              {embedded.map((item) => (
                <th key={item.id} title={item.text}>#{item.id}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {embedded.map((row) => (
              <tr key={row.id}>
                <th title={row.text}>#{row.id}</th>
                {embedded.map((column) => {
                  const cosine = cosineSimilarity(row.embedding as number[], column.embedding as number[]);
                  return (
                    <td
                      key={column.id}
                      style={similarityStyle(cosine)}
                      title={`${row.text} ↔ ${column.text}`}
                    >
                      {cosine.toFixed(3)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EmbeddingsExplorer: React.FC<{ versions: string[] }> = ({ versions }) => {
  const [inputText, setInputText] = useState('');
  const [modelVersion, setModelVersion] = useState(versions[versions.length - 1] ?? 'v3');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<EmbeddingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const texts = splitLines(inputText);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (texts.length === 0) {
      setError('Enter at least one line of text');
      return;
    }

    if (texts.length > MAX_ITEMS) {
      setError(`At most ${MAX_ITEMS} lines per request`);
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      setResult(await apiService.embedCargo(texts, modelVersion));
    } catch (err: any) {
      console.error('Embedding error:', err);
      setError(err.response?.data?.details || err.response?.data?.error || 'Failed to compute embeddings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setInputText('');
    setResult(null);
    setError(null);
  };

  return (
    <div className="content-layout">
      <section className="card form-card">
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label htmlFor="embeddingText" className="field-label">Cargo descriptions (one per line)</label>
            <textarea
              id="embeddingText"
              className="input textarea"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={'Lithium ion batteries\nPaint thinner\nCotton fabric'}
              rows={8}
              disabled={isLoading}
            />
            <small className="field-hint">{texts.length} line{texts.length === 1 ? '' : 's'}</small>
          </div>

          <div className="field">
            <label htmlFor="embeddingVersion" className="field-label">Embedding model</label>
            <select
              id="embeddingVersion"
              className="input select"
              value={modelVersion}
              onChange={(e) => setModelVersion(e.target.value)}
              disabled={isLoading}
            >
              {versions.map((version) => (
                <option key={version} value={version}>emb {version}</option>
              ))}
            </select>
          </div>

          <div className="form-actions">
            <button type="submit" disabled={isLoading || texts.length === 0} className="btn btn-primary btn-md">
              {isLoading ? 'Embedding…' : 'Get embeddings'}
            </button>
            <button type="button" onClick={handleClear} disabled={isLoading} className="btn btn-flat btn-md">
              Clear
            </button>
          </div>
        </form>
      </section>

      <section className="results-section">
        {error && (
          <div className="alert alert-error" role="alert">
            <p className="alert-title">Error</p>
            <p>{error}</p>
          </div>
        )}

        {result && (
          <div className="card result-card">
            <div className="result-header">
              <span className="h5">Embeddings</span>
              <span className="tag">emb {result.modelVersion}</span>
            </div>
            {result.items.map((item, index) => (
              <EmbeddingRow key={item.id} item={item} index={index} />
            ))}
            <SimilarityMatrix items={result.items} />
          </div>
        )}
      </section>
    </div>
  );
};

export default EmbeddingsExplorer;
